import React from 'react';
import { DialogTitle, Box, Typography } from '@mui/material';
import { Visibility, Edit, PersonAdd } from '@mui/icons-material';

interface FormHeaderProps {
  mode: 'view' | 'edit' | 'create' | 'createEmsUser';
}

const FormHeader: React.FC<FormHeaderProps> = ({ mode }) => {
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateEmsUserMode = mode === 'createEmsUser';

  const getTitle = (): string => {
    if (isViewMode) return 'View Candidate';
    if (isEditMode) return 'Edit Candidate';
    if (isCreateEmsUserMode) return 'Create EMS User';
    return 'Add Candidate';
  };

  const getDescription = (): string => {
    if (isViewMode) return 'View candidate information and details';
    if (isEditMode)
      return 'Update candidate information and employment details';
    if (isCreateEmsUserMode) return 'Create a new EMS user';
    return 'Add new candidate to the employee management system';
  };

  const getIcon = () => {
    if (isViewMode) return <Visibility />;
    if (isEditMode) return <Edit />;
    return <PersonAdd />;
  };

  const getColors = () => {
    return {
      background: '#e3f2fd',
      border: '#1976d2',
      text: '#1976d2',
      statusBg: '#e3f2fd',
      statusBorder: '#90caf9',
      statusText: '#1565c0'
    };
  };

  const colors = getColors();

  return (
    <DialogTitle
      sx={{
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
        py: { xs: 1, sm: 1.5, md: 2 },
        px: { xs: 1, sm: 1.5, md: 2 }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5, md: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: 32, sm: 40, md: 48 },
            height: { xs: 32, sm: 40, md: 48 },
            borderRadius: { xs: '8px', sm: '10px', md: '12px' },
            backgroundColor: colors.background,
            border: `2px solid ${colors.border}`,
            flexShrink: 0,
            '& .MuiSvgIcon-root': {
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
            }
          }}
        >
          {getIcon()}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0 // Prevents text overflow
          }}
        >
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              color: colors.text,
              letterSpacing: '0.3px',
              mb: { xs: 0.25, sm: 0.375, md: 0.5 },
              lineHeight: 1.2
            }}
          >
            {getTitle()}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
              fontWeight: 400,
              lineHeight: 1.3,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {getDescription()}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 0.75, md: 1 },
            px: { xs: 1, sm: 1.5, md: 2 },
            py: { xs: 0.5, sm: 0.75, md: 1 },
            borderRadius: { xs: '16px', sm: '18px', md: '20px' },
            backgroundColor: colors.statusBg,
            border: `1px solid ${colors.statusBorder}`,
            flexShrink: 0
          }}
        >
          <Box
            sx={{
              width: { xs: 6, sm: 7, md: 8 },
              height: { xs: 6, sm: 7, md: 8 },
              borderRadius: '50%',
              backgroundColor: colors.border
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: colors.statusText,
              fontWeight: 600,
              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              whiteSpace: 'nowrap'
            }}
          >
            {mode === 'createEmsUser' ? 'Create EMS User Mode' : `${mode} MODE`}
          </Typography>
        </Box>
      </Box>
    </DialogTitle>
  );
};

export default FormHeader;
